import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agentStatsApi,
  agentsApi,
  agentTemplatesApi,
  type Agent,
  type CreateAgentInput,
  type CreateAgentTemplateInput,
  type GenerateTemplateInput,
  type VoiceFilters,
} from "./api";

const KEY = ["agents"] as const;

export function useAgents() {
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: agentsApi.list,
    staleTime: 30_000,
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentsApi.remove(id),
    onSuccess: (_d, id) => {
      qc.setQueryData<Agent[]>([...KEY, "list"], (old) =>
        old?.filter((a) => a.id !== id),
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useDeployAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentsApi.deploy(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export function useDuplicateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentsApi.duplicate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export function useAgent(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "detail", id],
    queryFn: () => agentsApi.get(id!),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentInput) => agentsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export function useUpdateAgent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CreateAgentInput>) =>
      agentsApi.update(id, data),
    onSuccess: (updated) => {
      // The update endpoint returns a partial agent (no id / display fields),
      // so merge into the cached agent instead of replacing — otherwise id is
      // wiped and the next save PATCHes /agents/undefined/.
      qc.setQueryData([...KEY, "detail", id], (old?: Agent) =>
        old ? { ...old, ...updated } : updated,
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useVoiceCatalog() {
  return useQuery({
    queryKey: ["voice-catalog"],
    queryFn: agentsApi.voiceCatalog,
    staleTime: 5 * 60_000,
  });
}

/** Live voices from the provider's own API — refetched on every mount so the
 * picker always shows the provider's current list (server caches ~5 min). */
export function useProviderVoices(provider?: string, filters: VoiceFilters = {}) {
  return useQuery({
    queryKey: ["provider-voices", provider, filters],
    queryFn: () => agentsApi.providerVoices(provider!, filters),
    enabled: !!provider,
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    // Keep the previous list on screen while a new search/filter loads.
    placeholderData: (prev) => prev,
  });
}

export function useAgentTemplates() {
  return useQuery({
    queryKey: ["agent-templates", "list"],
    queryFn: agentTemplatesApi.list,
    staleTime: 5 * 60_000,
  });
}

export function useCreateAgentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentTemplateInput) =>
      agentTemplatesApi.create(data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["agent-templates", "list"] }),
  });
}

export function useDeleteAgentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentTemplatesApi.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["agent-templates", "list"] }),
  });
}

export function useGenerateAgentTemplate() {
  return useMutation({
    mutationFn: (data: GenerateTemplateInput) =>
      agentTemplatesApi.generate(data),
  });
}

export function useAgentStats(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "stats", id],
    queryFn: () => agentStatsApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}
